
<template>
  <Cos-Select
    v-model:value="state"
    v-bind="$attrs"
    :allow-clear="allowClear"
    :options="innerOptions"
    :placeholder="placeholder"
    :mode="mode"
    :max-tag-count="maxTagCount"
    :max-tag-text-length="maxTagTextLength"
    :show-arrow="showArrow"
    @change="handleChange"
    @dropdown-visible-change="handleVisibleChange"
  >
    <template #[slot]="data" v-for="slot in Object.keys($slots)">
      <slot :name="slot" v-bind="data || {}"></slot>
    </template>
    <template #suffixIcon v-if="loading || isMultipleMode">
      <LoadingOutlined v-if="loading" spin />
      <IconUpDown v-else />
    </template>
    <template v-if="loading" #notFoundContent>
      <Spin size="small" />
    </template>
  </Cos-Select>
</template>

<script lang="ts">
import { defineComponent, computed, ref, unref, watch, watchEffect, PropType } from 'vue'
import { Spin } from 'ant-design-vue'
import { LoadingOutlined } from '@ant-design/icons-vue'
import { bool, string, func, object, array, number, any } from 'vue-types'

import { useFormItem, defaultChangeEvent } from './hooks/index'
import type { Recordable } from './hooks/type'

import IconUpDown from './hooks/IconUpDown'

  type OptionItem = { value: string | number; label: string; disabeld?: boolean }
  type FieldNames = { label: string; value: string }

export default defineComponent({
  name: 'FbuApiSelect',
  inheritAttrs: false,
  props: {
    value: any(),
    allowClear: bool().def(true),
    api: func<(...args: any[]) => Promise<Recordable | OptionItem[]>>(),
    resultField: string().def('list'),
    numberToString: bool().def(false),
    fieldNames: object<FieldNames>().def({ label: 'label', value: 'value' }),
    params: object<Recordable>().def({}),
    immediate: bool().def(true),
    alwaysLoad: bool().def(false),
    placeholder: string().def('请选择'),
    options: array<Recordable>().def(() => []),
    mode: {
      type: String as PropType<'multiple' | 'tags' | 'SECRET_COMBOBOX_MODE_DO_NOT_USE'>
    }, // 'multiple' | 'tags' | 'SECRET_COMBOBOX_MODE_DO_NOT_USE'
    maxTagCount: number().def(1),
    maxTagTextLength: number().def(7),
    showArrow: bool().def(true)
  },
  components: {
    Spin,
    LoadingOutlined,
    IconUpDown
  },
  emits: [defaultChangeEvent, 'update:value', 'search', 'options-change'],
  setup(props, { emit }) {
    const loading = ref(false)
    const emitData = ref<any[]>([])
    const [state] = useFormItem({ props, emitData })
    const options = ref<Recordable[]>(props.options || [])
    const isFirstLoaded = ref(true)

    watchEffect(() => {
      props.immediate && !props.alwaysLoad && fetch()
    })

    watch(
      () => props.options,
      (newOptions) => {
        options.value = newOptions
      }
    )

    watch(
      () => props.params,
      () => {
        !unref(isFirstLoaded) && fetch()
      },
      { deep: true }
    )

    const innerOptions = computed(() => {
      const { fieldNames, numberToString } = props
      const { label, value } = fieldNames
      return unref(options).map((item) => {
        return {
          ...item,
          label: item[label],
          value: (numberToString && `${item[value]}`) || item[value]
        }
      })
    })

    const isMultipleMode = computed(() => props.mode === 'multiple' || props.mode === 'tags')

    async function fetch() {
      const { api } = props
      if (typeof api !== 'function') {
        return
      }
      options.value = []
      loading.value = true
      try {
        let result = await api(props.params)
        if (!result) {
          return
        }
        if (!Array.isArray(result)) {
          result = result[props.resultField]
        }
        options.value = (result || []) as OptionItem[]
        emit('options-change', result.slice(0))
      } catch (err) {
        console.error(err)
      } finally {
        loading.value = false
      }
    }

    function handleChange(value: any, ...args: any[]) {
      emit('update:value', value)
      emitData.value = args
    }
    function handleVisibleChange(open: boolean) {
      if (open) {
        if (props.alwaysLoad) {
          fetch()
        } else if (!props.immediate && unref(isFirstLoaded)) {
          fetch()
          isFirstLoaded.value = false
        }
      }
    }

    return {
      loading,
      state,
      innerOptions,
      handleChange,
      handleVisibleChange,
      isMultipleMode
    }
  }
})
</script>
